import { Suspense } from "react";
import { Route } from "react-router-dom";

import { ROUTES } from "@/constants/routes";
import { MALE_PARTNER_PRODUCT_COPY } from "@/pages/malePartner/constants";
import { lazy } from "@/utils/react/lazyWithRetries";

// Lazy load all public pages
const NotFound404 = lazy(() => import("@/pages/404"));
// Local-only component gallery (ADR 00074). The import sits inside the
// development check so production builds drop the chunk, not just the route.
const UIGallery =
  process.env.NODE_ENV === "development"
    ? lazy(() => import("@/pages/ui/UIGallery"))
    : null;
const Login = lazy(() => import("@/pages/auth/login"));
const PasswordReset = lazy(() => import("@/pages/auth/passwordReset"));
const PasswordResetConfirm = lazy(
  () => import("@/pages/auth/passwordResetConfirm")
);
const Register = lazy(() => import("@/pages/auth/register"));
const RegisterProvider = lazy(() =>
  import("@/pages/auth/registerProvider").then((module) => ({
    default: module.RegisterProvider,
  }))
);
const LoadingOrder = lazy(() => import("@/pages/loadingOrder"));
const ProviderMagicRedirect = lazy(
  () => import("@/pages/providerMagicRedirect")
);
const MalePartnerEligibility = lazy(
  () => import("@/pages/malePartner/eligibility")
);
const MalePartnerLogin = lazy(() => import("@/pages/malePartner/login"));
const MalePartnerRegister = lazy(() => import("@/pages/malePartner/register"));
const VerifyAccountOrderTransfer = lazy(
  () => import("@/pages/welcome/verifyAccountOrderTransfer")
);
const OrderTransferredConfirmation = lazy(
  () => import("@/pages/welcome/verifyAccountOrderTransferConfirm")
);
const VerifyEmailConfirm = lazy(
  () => import("@/pages/welcome/verifyEmailConfirm")
);

// Reusable loader for public pages
const PublicPageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="w-6 h-6 border-4 border-gray-200 border-t-gray-600 rounded-full animate-spin mx-auto mb-3"></div>
    </div>
  </div>
);

export const PublicRoutes = () => {
  return (
    <>
      <Route
        path="/loading/"
        element={
          <Suspense fallback={<PublicPageLoader />}>
            <LoadingOrder />
          </Suspense>
        }
      />
      <Route
        path="/login/"
        element={
          <Suspense fallback={<PublicPageLoader />}>
            <Login />
          </Suspense>
        }
      />
      <Route
        path="/register/"
        element={
          <Suspense fallback={<PublicPageLoader />}>
            <Register />
          </Suspense>
        }
      />
      <Route
        path="/register/male-partner/eligibility/"
        element={
          <Suspense fallback={<PublicPageLoader />}>
            <MalePartnerEligibility
              imageUrl={
                MALE_PARTNER_PRODUCT_COPY.DEFAULT_PRODUCT.BUNDLE_IMAGE_URL
              }
            />
          </Suspense>
        }
      />
      <Route
        path="/register/male-partner/"
        element={
          <Suspense fallback={<PublicPageLoader />}>
            <MalePartnerRegister
              imageUrl={
                MALE_PARTNER_PRODUCT_COPY.DEFAULT_PRODUCT.BUNDLE_IMAGE_URL
              }
            />
          </Suspense>
        }
      />
      <Route
        path="/login/male-partner/"
        element={
          <Suspense fallback={<PublicPageLoader />}>
            <MalePartnerLogin />
          </Suspense>
        }
      />
      <Route
        path="/treatments/login/"
        element={
          <Suspense fallback={<PublicPageLoader />}>
            <Login loginRoute="treatment" />
          </Suspense>
        }
      />
      <Route
        path="/treatments/register/"
        element={
          <Suspense fallback={<PublicPageLoader />}>
            <Register registrationRoute="treatment" />
          </Suspense>
        }
      />
      <Route
        path="/password-reset/"
        element={
          <Suspense fallback={<PublicPageLoader />}>
            <PasswordReset />
          </Suspense>
        }
      />
      <Route
        path="/password-reset-confirm/"
        element={
          <Suspense fallback={<PublicPageLoader />}>
            <PasswordResetConfirm />
          </Suspense>
        }
      />
      <Route
        path="/provider/register/"
        element={
          <Suspense fallback={<PublicPageLoader />}>
            <RegisterProvider />
          </Suspense>
        }
      />
      <Route
        path="/verify-email-confirm/"
        element={
          <Suspense fallback={<PublicPageLoader />}>
            <VerifyEmailConfirm />
          </Suspense>
        }
      />
      <Route
        path="/order-transfer-confirm/"
        element={
          <Suspense fallback={<PublicPageLoader />}>
            <OrderTransferredConfirmation />
          </Suspense>
        }
      />
      <Route
        path="/verify-account-order-transfer/"
        element={
          <Suspense fallback={<PublicPageLoader />}>
            <VerifyAccountOrderTransfer />
          </Suspense>
        }
      />
      <Route
        path={`${ROUTES.PROVIDER_TEST}:token`}
        element={
          <Suspense fallback={<PublicPageLoader />}>
            <ProviderMagicRedirect />
          </Suspense>
        }
      />
      {UIGallery ? (
        <Route
          path={ROUTES.UI_GALLERY}
          element={
            <Suspense fallback={<PublicPageLoader />}>
              <UIGallery />
            </Suspense>
          }
        />
      ) : null}
      <Route
        path="/404/"
        element={
          <Suspense fallback={<PublicPageLoader />}>
            <NotFound404 />
          </Suspense>
        }
      />
    </>
  );
};
